/**
 * Client-Side Routing Engine
 * Simulates intelligent routing without backend calls
 */

import type { PromptFeatures, RoutingDecision } from '@/lib/types';

// Model cost data (per 1M tokens)
const MODEL_COSTS = {
  'claude-sonnet-4-5-20250929': 3.0,
  'claude-haiku-4-5-20251001': 0.25,
  'claude-opus-4-1-20250805': 5.0,
  'gpt-5': 2.5,
  'gpt-5-mini': 0.5,
  'gemini-2.5-pro': 1.75,
  'gemini-2.5-flash': 0.3,
  'gemini-2.5-flash-lite': 0.1,
} as const;

/**
 * Estimate token count from text (rough approximation)
 */
function estimateTokenCount(text: string): number {
  // Rough estimate: ~4 characters per token
  return Math.ceil(text.length / 4);
}

/**
 * Extract features from prompt (without storing raw text)
 */
export function extractPromptFeatures(prompt: string): PromptFeatures {
  const length = prompt.length;
  const tokenCount = estimateTokenCount(prompt);

  // Heuristic complexity scoring
  const complexityIndicators = [
    /write.*code|function|class|implement/i.test(prompt),
    /explain|analyze|compare|evaluate/i.test(prompt),
    /creative|story|poem|generate/i.test(prompt),
    prompt.length > 500,
    /multi-step|complex|detailed/i.test(prompt),
  ];
  const complexityScore = complexityIndicators.filter(Boolean).length / 5;

  // Determine task type
  let taskType = 'General';
  if (/code|function|programming|debug/i.test(prompt)) {
    taskType = 'Code Generation';
  } else if (/creative|story|write|generate/i.test(prompt)) {
    taskType = 'Creative Writing';
  } else if (/analyze|explain|why|how|compare/i.test(prompt)) {
    taskType = 'Analysis';
  } else if (/translate|translation/i.test(prompt)) {
    taskType = 'Translation';
  } else if (/summarize|summary/i.test(prompt)) {
    taskType = 'Summarization';
  }

  return {
    length,
    tokenCount,
    complexityScore,
    taskType,
    requiresCode: /code|function|programming|debug/i.test(prompt),
    requiresCreativity: /creative|story|write|generate/i.test(prompt),
    requiresReasoning: /analyze|explain|why|how|compare/i.test(prompt),
    requiresMultimodal: /image|chart|graph|visual/i.test(prompt),
  };
}

/**
 * Generate SHA-256 hash (simplified for demo)
 */
export function generateHash(str: string): string {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash; // Convert to 32bit integer
  }
  return Math.abs(hash).toString(16).padStart(16, '0');
}

/**
 * Simulate routing decision
 */
export function simulateRoutingDecision(
  prompt: string,
  _policy: 'cost-optimized' | 'balanced' | 'quality-first' = 'balanced'
): RoutingDecision {
  const features = extractPromptFeatures(prompt);

  let selectedModel: RoutingDecision['selectedModel'];
  let reasoning: string[] = [];

  // Decision logic based on heuristics
  if (features.requiresCode && features.complexityScore > 0.5) {
    selectedModel = {
      id: 'claude-sonnet-4-5-20250929',
      name: 'Claude Sonnet 4.5',
      provider: 'Anthropic',
    };
    reasoning = [
      'Code generation detected in prompt',
      `High complexity score (${features.complexityScore.toFixed(2)}) requires advanced reasoning`,
      'Claude Sonnet 4.5 is SOTA for coding tasks',
    ];
  } else if (features.complexityScore < 0.3) {
    selectedModel = {
      id: 'gemini-2.5-flash-lite',
      name: 'Gemini 2.5 Flash-Lite',
      provider: 'Google',
    };
    reasoning = [
      `Simple query with low complexity (${features.complexityScore.toFixed(2)})`,
      'Cost-optimized routing selected',
      'Gemini Flash-Lite provides 95% cost savings',
    ];
  } else if (features.requiresCreativity) {
    selectedModel = {
      id: 'gpt-5',
      name: 'GPT-5',
      provider: 'OpenAI',
    };
    reasoning = [
      'Creative writing task detected',
      'GPT-5 excels at creative content',
      'Balanced cost-quality tradeoff',
    ];
  } else if (features.taskType === 'Summarization') {
    selectedModel = {
      id: 'claude-haiku-4-5-20251001',
      name: 'Claude Haiku 4.5',
      provider: 'Anthropic',
    };
    reasoning = [
      'Summarization task detected',
      'Claude Haiku optimized for large context',
      'Fast processing with high accuracy',
    ];
  } else {
    selectedModel = {
      id: 'gemini-2.5-flash',
      name: 'Gemini 2.5 Flash',
      provider: 'Google',
    };
    reasoning = [
      'General-purpose query',
      'Gemini Flash provides best price-performance',
      'Fast response time (<100ms)',
    ];
  }

  // Calculate costs and savings
  const selectedCost = MODEL_COSTS[selectedModel.id as keyof typeof MODEL_COSTS];
  const premiumCost = Math.max(...Object.values(MODEL_COSTS));
  const openRouterMarkup = 1.15; // 15% markup
  const liteLLMMarkup = 1.08; // 8% markup

  return {
    selectedModel,
    reasoning,
    confidence: 0.85 + Math.random() * 0.14, // 0.85-0.99
    estimatedCost: selectedCost,
    estimatedLatency: 50 + Math.random() * 100, // 50-150ms
    savings: {
      vsAlwaysPremium: Math.round((1 - selectedCost / premiumCost) * 100),
      vsOpenRouter: Math.round(
        ((premiumCost * openRouterMarkup - selectedCost) / (premiumCost * openRouterMarkup)) * 100
      ),
      vsLiteLLM: Math.round(
        ((premiumCost * liteLLMMarkup - selectedCost) / (premiumCost * liteLLMMarkup)) * 100
      ),
    },
  };
}

/**
 * Async wrapper to simulate network delay
 */
export async function analyzePrompt(
  prompt: string,
  policy?: 'cost-optimized' | 'balanced' | 'quality-first'
): Promise<RoutingDecision> {
  // Simulate network latency (500-1500ms)
  const delay = 500 + Math.random() * 1000;
  await new Promise((resolve) => setTimeout(resolve, delay));

  return simulateRoutingDecision(prompt, policy);
}
